import {
  useMutationCreateUiPreference,
  useMutationUpdateUiPreference,
  useQueryGetAllUiPreferences,
} from "@apis:ui_preferences/ui_preferences";
import i18n, { SupportedLanguage } from "@libs:i18n";
import { useUser } from "@libs:react-query-auth";
import { useMemo } from "react";
import { CamelCasedPropertiesDeep } from "type-fest";

import { UserPreferenceSchema } from "@:openapi";

// Camel-cased shape of the stored user preference, matching the generated schema.
type UserPreference = CamelCasedPropertiesDeep<UserPreferenceSchema>;

/**
 * Reads and writes the user's preferred UI language.
 * The choice is applied to i18next immediately.
 * When a user is signed in, it is also persisted as UiPreference.
 * On the login page, it only changes the language locally.
 */
export const useLanguagePreference = () => {
  const user = useUser().data;
  const userId = user?.id;

  const {
    data: uiPreferences,
    isLoading,
    refetch,
  } = useQueryGetAllUiPreferences(true, userId, undefined, {
    enabled: !!userId,
  });

  const createMutation = useMutationCreateUiPreference({
    onSuccess: () => {
      refetch();
    },
  });

  const updateMutation = useMutationUpdateUiPreference({
    onSuccess: () => {
      refetch();
    },
  });

  const userPreference = useMemo(
    () => uiPreferences?.find((pref) => pref.userId === userId && !pref.botId),
    [uiPreferences, userId],
  );

  const language = (userPreference?.preference as UserPreference | undefined)
    ?.language;

  const setLanguage = (next: SupportedLanguage) => {
    // Apply immediately in-memory.
    // The account preference persisted below is the real source of truth (no localStorage; see libs/i18n detection).
    i18n.changeLanguage(next);

    // Persist to the account only when signed in.
    if (!userId) {
      return;
    }

    const language = next as NonNullable<UserPreference["language"]>;

    if (userPreference) {
      const current = userPreference.preference as UserPreference;
      updateMutation.mutate({
        id: userPreference.id,
        preference: { ...current, language: language },
      });
    } else {
      createMutation.mutate({
        userId,
        botId: null,
        preference: { language: language },
      });
    }
  };

  return { language, setLanguage, isLoading };
};
